/* Chinese zodiac tag emission.
   Signal layer kept separate from the user-facing copy in
   starChartChineseContent.js: tag keys are semantic snake_case, never the
   display text, never the animal name.

   Four contributing layers (year animal, element combo, inner animal from
   birth month, secret animal from birth hour). Inner and secret lean heavily
   `latent` because they describe under-the-surface tendencies, not the
   relational contributions a partner would meet first.

   tag_type: "offers" (a contribution they bring) | "needs" (what they need
   from a partner) | "latent" (under-the-surface, for synthesis not matching).

   Provenance is carried in source_module ("star_chart_chinese") because
   user_tags has no source_submodule column and saveTags upserts on
   (user_id, tag_key, tag_type). The collector dedups by tag_key|tag_type so a
   single upsert never hits a duplicate conflict target. */

// ── Year animal (12) — highest signal, richest sets ────────────────────────
export const CHINESE_ANIMAL_TAGS = {
  rat: [
    { key: "reads_rooms", type: "offers" },
    { key: "network_builder", type: "offers" },
    { key: "resourceful_under_pressure", type: "offers" },
    { key: "status_attuned", type: "latent" },
    { key: "keeps_options_open", type: "latent" },
    { key: "guarded_trust", type: "latent" },
  ],
  ox: [
    { key: "dependable", type: "offers" },
    { key: "long_haul_stamina", type: "offers" },
    { key: "grounded_presence", type: "offers" },
    { key: "stubborn_streak", type: "latent" },
    { key: "wary_of_change", type: "latent" },
  ],
  tiger: [
    { key: "courage_first", type: "offers" },
    { key: "magnetic_presence", type: "offers" },
    { key: "fiercely_protective", type: "offers" },
    { key: "burns_hot_short", type: "latent" },
    { key: "impatient_with_stillness", type: "latent" },
  ],
  rabbit: [
    { key: "diplomatic", type: "offers" },
    { key: "emotionally_perceptive", type: "offers" },
    { key: "refined_taste", type: "offers" },
    { key: "conflict_avoidant", type: "latent" },
    { key: "withdraws_under_strain", type: "latent" },
  ],
  dragon: [
    { key: "visionary_drive", type: "offers" },
    { key: "lifts_others", type: "offers" },
    { key: "natural_authority", type: "offers" },
    { key: "grandiosity_risk", type: "latent" },
    { key: "resists_correction", type: "latent" },
  ],
  snake: [
    { key: "sharp_insight", type: "offers" },
    { key: "strategic_mind", type: "offers" },
    { key: "loyal_to_a_chosen_few", type: "offers" },
    { key: "private_to_a_fault", type: "latent" },
    { key: "holds_slights", type: "latent" },
  ],
  horse: [
    { key: "passionate_energy", type: "offers" },
    { key: "natural_encourager", type: "offers" },
    { key: "learns_fast", type: "offers" },
    { key: "needs_freedom", type: "needs" },
    { key: "restless_starter", type: "latent" },
    { key: "impulsive_streak", type: "latent" },
  ],
  goat: [
    { key: "compassionate", type: "offers" },
    { key: "creative_sensibility", type: "offers" },
    { key: "quietly_principled", type: "offers" },
    { key: "indecisive_under_pressure", type: "latent" },
    { key: "withholds_when_hurt", type: "latent" },
  ],
  monkey: [
    { key: "inventive_problem_solver", type: "offers" },
    { key: "quick_wit", type: "offers" },
    { key: "endlessly_curious", type: "offers" },
    { key: "bores_easily", type: "latent" },
    { key: "sidesteps_depth", type: "latent" },
  ],
  rooster: [
    { key: "exacting_eye", type: "offers" },
    { key: "unvarnished_honesty", type: "offers" },
    { key: "loyal_completely", type: "offers" },
    { key: "hypercritical_risk", type: "latent" },
    { key: "perfectionism", type: "latent" },
  ],
  dog: [
    { key: "strong_fairness", type: "offers" },
    { key: "steadfast", type: "offers" },
    { key: "protective_of_others", type: "offers" },
    { key: "slow_to_trust_new_people", type: "latent" },
    { key: "holds_grudges", type: "latent" },
    { key: "watchful_anxiety", type: "latent" },
  ],
  pig: [
    { key: "genuine_warmth", type: "offers" },
    { key: "uncalculated_generosity", type: "offers" },
    { key: "trusts_by_default", type: "offers" },
    { key: "naive_to_angles", type: "latent" },
    { key: "overindulgent_streak", type: "latent" },
  ],
};

// ── Element combos (60) — how the element shifts the animal. 1-2 tags each,
//    not a restatement of the animal's own tags. Key format "element-animal". ─
export const CHINESE_COMBO_TAGS = {
  // Wood — growth, nurture, flexibility, building
  "wood-rat": [{ key: "builds_coalitions", type: "offers" }, { key: "humanizing_warmth", type: "latent" }],
  "wood-ox": [{ key: "nurtures_over_time", type: "offers" }, { key: "flexible_backbone", type: "latent" }],
  "wood-tiger": [{ key: "inspires_not_intimidates", type: "offers" }, { key: "growth_seeking", type: "latent" }],
  "wood-rabbit": [{ key: "generative_creativity", type: "offers" }, { key: "rooted_sensitivity", type: "latent" }],
  "wood-dragon": [{ key: "patient_vision", type: "offers" }, { key: "builds_what_grows", type: "latent" }],
  "wood-snake": [{ key: "shares_wisdom_openly", type: "offers" }, { key: "teaching_instinct", type: "latent" }],
  "wood-horse": [{ key: "commits_to_potential", type: "offers" }, { key: "evolves_in_place", type: "latent" }],
  "wood-goat": [{ key: "makes_things", type: "offers" }, { key: "shapes_spaces", type: "latent" }],
  "wood-monkey": [{ key: "follows_through", type: "offers" }, { key: "develops_ideas_slowly", type: "latent" }],
  "wood-rooster": [{ key: "precision_serves_beauty", type: "offers" }, { key: "maker_instinct", type: "latent" }],
  "wood-dog": [{ key: "protects_communities", type: "offers" }, { key: "builds_fair_structures", type: "latent" }],
  "wood-pig": [{ key: "cultivates_abundance", type: "offers" }, { key: "nurtures_what_lives", type: "latent" }],

  // Fire — heat, visibility, performance, intensity
  "fire-rat": [{ key: "acts_on_opportunity", type: "offers" }, { key: "competitive_instinct", type: "latent" }],
  "fire-ox": [{ key: "all_in_when_committed", type: "offers" }, { key: "passion_under_patience", type: "latent" }],
  "fire-tiger": [{ key: "impossible_to_ignore", type: "latent" }, { key: "theatrical_courage", type: "latent" }],
  "fire-rabbit": [{ key: "spine_under_grace", type: "offers" }, { key: "speaks_hard_truths", type: "latent" }],
  "fire-dragon": [{ key: "changes_the_room", type: "offers" }, { key: "commanding_radiance", type: "latent" }],
  "fire-snake": [{ key: "public_voice", type: "offers" }, { key: "performs_insight", type: "latent" }],
  "fire-horse": [{ key: "incandescent_energy", type: "latent" }, { key: "needs_real_rest", type: "needs" }],
  "fire-goat": [{ key: "expressive_art", type: "offers" }, { key: "unafraid_to_want", type: "latent" }],
  "fire-monkey": [{ key: "holds_the_room", type: "offers" }, { key: "contagious_charm", type: "latent" }],
  "fire-rooster": [{ key: "leads_by_excellence", type: "offers" }, { key: "generous_spotlight", type: "latent" }],
  "fire-dog": [{ key: "activist_edge", type: "offers" }, { key: "mobilized_by_injustice", type: "latent" }],
  "fire-pig": [{ key: "hosting_warmth", type: "offers" }, { key: "draws_people_in", type: "latent" }],

  // Earth — grounding, durability, practicality, building
  "earth-rat": [{ key: "long_game_builder", type: "offers" }, { key: "compounding_patience", type: "latent" }],
  "earth-ox": [{ key: "unshakeable_grounding", type: "offers" }, { key: "rigidity_risk", type: "latent" }],
  "earth-tiger": [{ key: "prepared_boldness", type: "offers" }, { key: "deliberate_risk", type: "latent" }],
  "earth-rabbit": [{ key: "builds_beauty", type: "offers" }, { key: "grounded_not_fragile", type: "latent" }],
  "earth-dragon": [{ key: "institution_builder", type: "offers" }, { key: "brick_by_brick", type: "latent" }],
  "earth-snake": [{ key: "domain_mastery", type: "offers" }, { key: "applied_depth", type: "latent" }],
  "earth-horse": [{ key: "deep_commitments", type: "offers" }, { key: "purposeful_movement", type: "latent" }],
  "earth-goat": [{ key: "quiet_infrastructure", type: "offers" }, { key: "sustaining_presence", type: "latent" }],
  "earth-monkey": [{ key: "durable_inventiveness", type: "offers" }, { key: "finishes_what_starts", type: "latent" }],
  "earth-rooster": [{ key: "steward_not_critic", type: "offers" }, { key: "builds_traditions", type: "latent" }],
  "earth-dog": [{ key: "reliable_rock", type: "offers" }, { key: "shows_up_daily", type: "latent" }],
  "earth-pig": [{ key: "lasting_home", type: "offers" }, { key: "rooted_generosity", type: "latent" }],

  // Metal — sharpness, discipline, ambition, precision
  "metal-rat": [{ key: "disciplined_ambition", type: "offers" }, { key: "uncompromising_quality", type: "latent" }],
  "metal-ox": [{ key: "directed_stamina", type: "offers" }, { key: "wants_excellence", type: "latent" }],
  "metal-tiger": [{ key: "strategic_courage", type: "offers" }, { key: "sharp_timing", type: "latent" }],
  "metal-rabbit": [{ key: "firm_boundary", type: "offers" }, { key: "steel_under_grace", type: "latent" }],
  "metal-dragon": [{ key: "cuts_through_obstacles", type: "offers" }, { key: "exacting_standards", type: "latent" }],
  "metal-snake": [{ key: "decisive_willpower", type: "offers" }, { key: "rarely_misses", type: "latent" }],
  "metal-horse": [{ key: "directed_passion", type: "offers" }, { key: "wins_not_just_runs", type: "latent" }],
  "metal-goat": [{ key: "structural_spine", type: "offers" }, { key: "bounded_compassion", type: "latent" }],
  "metal-monkey": [{ key: "refined_mastery", type: "offers" }, { key: "sharpens_with_age", type: "latent" }],
  "metal-rooster": [{ key: "tells_hard_truths", type: "offers" }, { key: "brilliant_demanding", type: "latent" }],
  "metal-dog": [{ key: "enforces_fairness", type: "offers" }, { key: "loyalty_with_teeth", type: "latent" }],
  "metal-pig": [{ key: "discerning_abundance", type: "offers" }, { key: "firm_limits", type: "latent" }],

  // Water — depth, intuition, emotional fluency
  "water-rat": [{ key: "trust_based_networks", type: "offers" }, { key: "feels_the_unsaid", type: "latent" }],
  "water-ox": [{ key: "moves_with_feeling", type: "offers" }, { key: "makes_others_heard", type: "latent" }],
  "water-tiger": [{ key: "soulful_leadership", type: "offers" }, { key: "emotionally_intelligent", type: "latent" }],
  "water-rabbit": [{ key: "moves_people", type: "offers" }, { key: "near_telepathic_perception", type: "latent" }],
  "water-dragon": [{ key: "subtle_power", type: "latent" }, { key: "works_the_subconscious", type: "latent" }],
  "water-snake": [{ key: "sees_undercurrents", type: "latent" }, { key: "oracular_intuition", type: "latent" }],
  "water-horse": [{ key: "deep_feeling_in_motion", type: "offers" }, { key: "tidal_passions", type: "latent" }],
  "water-goat": [{ key: "bottomless_empathy", type: "offers" }, { key: "guards_boundaries", type: "needs" }],
  "water-monkey": [{ key: "emotionally_wise_humor", type: "offers" }, { key: "understands_grief", type: "latent" }],
  "water-rooster": [{ key: "kind_honesty", type: "offers" }, { key: "knows_when_precision_wounds", type: "latent" }],
  "water-dog": [{ key: "understands_not_just_protects", type: "offers" }, { key: "reads_subtle_harm", type: "latent" }],
  "water-pig": [{ key: "soulful_generosity", type: "offers" }, { key: "sacred_pleasure", type: "latent" }],
};

// ── Inner animal (12) — under-the-surface, from birth month. Lean latent. ───
export const CHINESE_INNER_TAGS = {
  rat: [{ key: "background_calculation", type: "latent" }, { key: "tracks_trust_privately", type: "latent" }],
  ox: [{ key: "inner_durability", type: "latent" }, { key: "refuses_to_quit", type: "latent" }],
  tiger: [{ key: "confronts_when_needed", type: "latent" }, { key: "leaps_when_waiting_fails", type: "latent" }],
  rabbit: [{ key: "registers_emotional_weather", type: "latent" }, { key: "aesthetic_compass", type: "latent" }],
  dragon: [{ key: "refuses_to_settle_small", type: "latent" }, { key: "unspoken_ambition", type: "latent" }],
  snake: [{ key: "sees_patterns", type: "latent" }, { key: "holds_more_than_shown", type: "latent" }],
  horse: [{ key: "needs_air", type: "latent" }, { key: "restless_in_cages", type: "latent" }],
  goat: [{ key: "private_tenderness", type: "latent" }, { key: "creative_source", type: "latent" }],
  monkey: [{ key: "finds_the_angle", type: "latent" }, { key: "cannot_take_it_fully_seriously", type: "latent" }],
  rooster: [{ key: "internal_editor", type: "latent" }, { key: "private_high_standards", type: "latent" }],
  dog: [{ key: "moral_backbone", type: "latent" }, { key: "keeps_quiet_promises", type: "latent" }],
  pig: [{ key: "capacity_for_pleasure", type: "latent" }, { key: "default_trust", type: "latent" }],
};

// ── Secret animal (12) — most private, from birth hour. Heavily latent. ─────
export const CHINESE_SECRET_TAGS = {
  rat: [{ key: "always_assessing", type: "latent" }, { key: "private_contingency", type: "latent" }],
  ox: [{ key: "unbreakable_will", type: "latent" }, { key: "keeps_going_alone", type: "latent" }],
  tiger: [{ key: "hidden_fierceness", type: "latent" }, { key: "burns_bridges_for_love", type: "latent" }],
  rabbit: [{ key: "innermost_sensitivity", type: "latent" }, { key: "retreats_to_beauty", type: "latent" }],
  dragon: [{ key: "private_sense_of_destiny", type: "latent" }, { key: "fire_that_wont_go_out", type: "latent" }],
  snake: [{ key: "silent_long_strategist", type: "latent" }, { key: "keeps_secrets_from_self", type: "latent" }],
  horse: [{ key: "carries_wildness", type: "latent" }, { key: "dreams_of_running", type: "latent" }],
  goat: [{ key: "tender_interior", type: "latent" }, { key: "grieves_unseen", type: "latent" }],
  monkey: [{ key: "private_trickster", type: "latent" }, { key: "mental_games_for_amusement", type: "latent" }],
  rooster: [{ key: "private_self_correction", type: "latent" }, { key: "harder_on_self_than_others", type: "latent" }],
  dog: [{ key: "private_loyalty_code", type: "latent" }, { key: "keeps_vows_quietly", type: "latent" }],
  pig: [{ key: "private_warmth", type: "latent" }, { key: "kinder_than_shown", type: "latent" }],
};

/* Collect deduped tag rows for a computed Chinese chart.
   chineseData = { animal:{key}, element:{key}, innerAnimal:{key}, secretAnimal:{key}|null }
   Dedup by tag_key|tag_type (first layer wins) so the saveTags upsert on
   (user_id, tag_key, tag_type) never sees a duplicate conflict target. */
export function collectChineseTags(chineseData) {
  if (!chineseData) return [];
  const { animal, element, innerAnimal, secretAnimal } = chineseData;
  const comboKey = element && animal ? `${element.key}-${animal.key}` : null;

  const layers = [
    animal && CHINESE_ANIMAL_TAGS[animal.key]
      ? { tags: CHINESE_ANIMAL_TAGS[animal.key], value: animal.key }
      : null,
    comboKey && CHINESE_COMBO_TAGS[comboKey]
      ? { tags: CHINESE_COMBO_TAGS[comboKey], value: comboKey }
      : null,
    innerAnimal && CHINESE_INNER_TAGS[innerAnimal.key]
      ? { tags: CHINESE_INNER_TAGS[innerAnimal.key], value: innerAnimal.key }
      : null,
    secretAnimal && CHINESE_SECRET_TAGS[secretAnimal.key]
      ? { tags: CHINESE_SECRET_TAGS[secretAnimal.key], value: secretAnimal.key }
      : null,
  ].filter(Boolean);

  const seen = new Map();
  for (const layer of layers) {
    for (const t of layer.tags) {
      const k = `${t.key}|${t.type}`;
      if (!seen.has(k)) {
        seen.set(k, {
          tag_key: t.key,
          tag_value: layer.value,
          tag_type: t.type,
          source_module: "star_chart_chinese",
        });
      }
    }
  }
  return Array.from(seen.values());
}
